import { propertyType, t } from '@blocksuite/affine/blocks/database';
import zod from 'zod';

// Property Type Factory
export const rollupPropertyType = propertyType('rollup');

// Aggregation Types
export const RollupAggregationSchema = zod.enum([
  'show_original',
  'show_unique',
  'count',
  'count_values',
  'count_unique',
  'sum',
  'average',
  'min',
  'max',
  'percent_empty',
  'percent_filled',
  'concatenate',
]);

export type RollupAggregation = zod.infer<typeof RollupAggregationSchema>;

// Zod Schema fuer Property Data (Column-Konfiguration)
export const RollupPropertyDataSchema = zod.object({
  sourceRelationFieldId: zod.string(),
  targetFieldId: zod.string(),
  aggregation: RollupAggregationSchema,
  separator: zod.string().optional(),
});

export type RollupPropertyData = zod.infer<typeof RollupPropertyDataSchema>;

// Cell Value Schema - Computed value (read-only)
// Can be number, string, or array depending on aggregation
export const RollupCellRawValueSchema = zod
  .union([zod.number(), zod.string(), zod.array(zod.string())])
  .nullable();

export const RollupCellJsonValueSchema = zod
  .union([zod.number(), zod.string(), zod.array(zod.string())])
  .nullable();

export type RollupCellRawValue = zod.infer<typeof RollupCellRawValueSchema>;
export type RollupCellJsonValue = zod.infer<typeof RollupCellJsonValueSchema>;

// Model Config
export const rollupPropertyModelConfig = rollupPropertyType.modelConfig({
  name: 'Rollup',
  propertyData: {
    schema: RollupPropertyDataSchema,
    default: () => ({
      sourceRelationFieldId: '',
      targetFieldId: '',
      aggregation: 'show_original' as const,
    }),
  },
  rawValue: {
    schema: RollupCellRawValueSchema,
    default: () => null as RollupCellRawValue,
    fromString: ({ value }) => ({
      value: value ? parseFloat(value) || value : null,
    }),
    toString: ({ value }) => {
      if (value === null || value === undefined) return '';
      if (Array.isArray(value)) return value.join(', ');
      return String(value);
    },
    toJson: ({ value }) => value,
    fromJson: ({ value }) => value,
  },
  jsonValue: {
    schema: RollupCellJsonValueSchema,
    type: () => t.unknown.instance(),
    isEmpty: ({ value }) => value === null || value === undefined,
  },
});
